import React from 'react'
import {FileText, BookOpen, Code2, FileCode, Scan, FileType, Edit3, Search, Wand2} from 'lucide-react'

type ToolCardProps = {
  id: string
  linkTo?: string
  icon: React.ReactNode
  badge: string
  badgeColor: string
  title: string
  tagline: string
  description: string
  kd: string
  vol: string
  cpc?: string
  iconBg: string
  cta: string
}

function ToolCard({ id, linkTo, icon, badge, badgeColor, title, tagline, description, kd, vol, cpc, iconBg, cta }: ToolCardProps) {
  return (
    <article
      id={`card-${id}`}
      className="group bg-white rounded-2xl p-7 border border-gray-100 hover:-translate-y-1 transition-all duration-200"
      style={{ boxShadow: 'var(--shadow-card)' }}
      aria-labelledby={`${id}-title`}
    >
      <div className="flex items-start justify-between mb-5">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badgeColor}`}>
          {badge}
        </span>
      </div>

      <h3
        id={`${id}-title`}
        className="text-base font-bold text-gray-900 mb-1"
      >
        {title}
      </h3>
      <p className="text-sm text-indigo-600 font-medium mb-3">{tagline}</p>
      <p className="text-sm text-gray-500 font-light leading-relaxed mb-5">{description}</p>

      <div
        className="flex flex-wrap gap-2 mb-5"
        aria-label="SEO metrics"
      >
        <span
          className="font-mono-data text-xs bg-gray-50 text-gray-500 border border-gray-100 rounded-lg px-2.5 py-1"
          title="Monthly search volume"
        >
          {vol} vol/mo
        </span>
        <span
          className="font-mono-data text-xs bg-gray-50 text-gray-500 border border-gray-100 rounded-lg px-2.5 py-1"
          title="Keyword difficulty"
        >
          KD {kd}
        </span>
        {cpc && (
          <span
            className="font-mono-data text-xs bg-amber-50 text-amber-600 border border-amber-100 rounded-lg px-2.5 py-1"
            title="Cost per click"
          >
            CPC {cpc}
          </span>
        )}
      </div>

      {linkTo ? (
        <a
          href={linkTo}
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-indigo-600 border-2 border-indigo-100 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center"
          aria-label={cta}
        >
          {cta}
        </a>
      ) : (
        <button
          type="button"
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-indigo-600 border-2 border-indigo-100 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          aria-label={cta}
        >
          {cta}
        </button>
      )}
    </article>
  )
}

const TOOLS: ToolCardProps[] = [
  {
    id: 'pdf-to-png',
    linkTo: '#pdf-to-png',
    icon: <FileText size={22} className="text-indigo-600" />,
    badge: '74K vol/mo',
    badgeColor: 'bg-indigo-50 text-indigo-700',
    title: 'PDF to PNG',
    tagline: '74,000 monthly searches · KD 8',
    description: 'Render each PDF page as a high-resolution PNG using canvas layers and export them bundled inside a local .zip file — no server uploads.',
    kd: '8',
    vol: '74,000',
    iconBg: 'bg-indigo-50',
    cta: 'Convert PDF to PNG →',
  },
  {
    id: 'epub-to-pdf',
    linkTo: '#epub-to-pdf',
    icon: <BookOpen size={22} className="text-emerald-600" />,
    badge: '90.5K vol/mo',
    badgeColor: 'bg-emerald-50 text-emerald-700',
    title: 'EPUB to PDF',
    tagline: '90,500 monthly searches · KD 24',
    description: 'Parse eBook content and reflow it into standard printable PDF margins. Preserves headings, chapter breaks, and embedded images.',
    kd: '24',
    vol: '90,500',
    iconBg: 'bg-emerald-50',
    cta: 'Open EPUB Converter →',
  },
  {
    id: 'md-to-pdf',
    linkTo: '#md-to-pdf',
    icon: <FileCode size={22} className="text-violet-600" />,
    badge: '12.1K vol/mo',
    badgeColor: 'bg-violet-50 text-violet-700',
    title: 'Markdown to PDF',
    tagline: '12,100 monthly searches · KD 1',
    description: 'A split-screen Markdown workspace with live rendering. One click exports a download-ready, print-formatted PDF document.',
    kd: '1',
    vol: '12,100',
    iconBg: 'bg-violet-50',
    cta: 'Open MD Editor →',
  },
  {
    id: 'ipynb-to-pdf',
    icon: <Code2 size={22} className="text-amber-600" />,
    badge: '6.6K vol/mo',
    badgeColor: 'bg-amber-50 text-amber-700',
    title: 'IPYNB to PDF',
    tagline: '6,600 monthly searches · KD 0',
    description: 'Custom handler that renders Jupyter notebook cells, code outputs, and matplotlib plots as clean, shareable PDF documents.',
    kd: '0',
    vol: '6,600',
    iconBg: 'bg-amber-50',
    cta: 'Convert Notebook →',
  },
  {
    id: 'pages-to-pdf',
    icon: <FileText size={22} className="text-sky-600" />,
    badge: '9.9K vol/mo',
    badgeColor: 'bg-sky-50 text-sky-700',
    title: 'Pages to PDF',
    tagline: '9,900 monthly searches · KD 6',
    description: 'Upload Apple .pages documents and convert them to universally readable PDF files. Preserves fonts, tables, and layout fidelity.',
    kd: '6',
    vol: '9,900',
    iconBg: 'bg-sky-50',
    cta: 'Convert .pages File →',
  },
  {
    id: 'xml-to-pdf',
    icon: <FileType size={22} className="text-rose-600" />,
    badge: '$2.42 CPC',
    badgeColor: 'bg-rose-50 text-rose-700',
    title: 'XML to PDF',
    tagline: 'High CPC · $2.42 · KD 2',
    description: 'Transform invoice and report XML schemas into print-ready PDF sheets. Supports XSLT stylesheets for custom column layouts.',
    kd: '2',
    vol: 'High CPC',
    cpc: '$2.42',
    iconBg: 'bg-rose-50',
    cta: 'Convert XML to PDF →',
  },
  {
    id: 'pdf-editor',
    icon: <Edit3 size={22} className="text-fuchsia-600" />,
    badge: '$8.17 CPC',
    badgeColor: 'bg-fuchsia-50 text-fuchsia-700',
    title: 'PDF Editor (Mac / Web)',
    tagline: '$8.17 CPC · Premium Enterprise · KD 52',
    description: 'Modify page orders, append headers and footers, and edit text directly on client-side canvas — the highest-value tool in the portfolio.',
    kd: '52',
    vol: 'Premium',
    cpc: '$8.17',
    iconBg: 'bg-fuchsia-50',
    cta: 'Open PDF Editor →',
  },
  {
    id: 'make-searchable',
    icon: <Search size={22} className="text-teal-600" />,
    badge: '$2.20 CPC',
    badgeColor: 'bg-teal-50 text-teal-700',
    title: 'Make PDF Searchable',
    tagline: '$2.20 CPC · WASM OCR · KD 25',
    description: 'Run Tesseract.js OCR on scanned PDFs using WebAssembly threads. Outputs a searchable, copy-pasteable PDF — no cloud dependency.',
    kd: '25',
    vol: 'High CPC',
    cpc: '$2.20',
    iconBg: 'bg-teal-50',
    cta: 'Run OCR →',
  },
  {
    id: 'pdf-to-scanned',
    icon: <Scan size={22} className="text-orange-600" />,
    badge: 'Viral Niche',
    badgeColor: 'bg-orange-50 text-orange-700',
    title: 'PDF to Scanned PDF',
    tagline: '260 vol · KD 9 · Zero Competition',
    description: 'Canvas filter skews pages slightly, grayscales, and adds grain/noise — making digital PDFs look authentically scanned for admin requirements.',
    kd: '9',
    vol: '260',
    iconBg: 'bg-orange-50',
    cta: 'Apply Scan Effect →',
  },
  {
    id: 'mupdf-comparison',
    icon: <Wand2 size={22} className="text-gray-600" />,
    badge: '$16.35 CPC',
    badgeColor: 'bg-gray-100 text-gray-700',
    title: 'MuPDF Web Compilation',
    tagline: '$16.35 CPC — highest in portfolio · KD 2',
    description: 'Technical comparison page pitching PdfTide web compilation vs local MuPDF drivers. Targets engineers evaluating rendering libraries.',
    kd: '2',
    vol: 'Enterprise',
    cpc: '$16.35',
    iconBg: 'bg-gray-100',
    cta: 'View Comparison →',
  },
]

export default function ToolGrid() {
  return (
    <section
      id="tools"
      className="py-24 px-4"
      aria-labelledby="tools-heading"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-3">
            The Tool Suite
          </p>
          <h2
            id="tools-heading"
            className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-4"
          >
            10 high-yield PDF tools
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto font-light">
            Selected for maximum search volume, lowest keyword difficulty, and highest
            advertiser intent. All processing runs client-side.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" role="list">
          {TOOLS.map((tool) => (
            <div key={tool.id} role="listitem">
              <ToolCard {...tool} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
